import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, skip } from 'rxjs/operators';
import { NotificationDesignCapabilitiesDto } from '../models';
import { AppProperty } from '../properties/app.property';
import { NotifierTemplatesService } from './notifier-templates.service';

/**
 * Se l'utente gestisce i testi delle email dell'azienda corrente (#2103): lo decide il Notifier
 * (`GET /notifier/v1/design/capabilities`), non Workspace /v1/me, perché la regola guarda i ruoli
 * dell'app Notifier e in impersonation quelli di chi impersona e dell'impersonato.
 *
 * La risposta resta in memoria. Il cambio di azienda e l'impersonation avviata o tolta dalla shell la
 * ricaricano (`window.location`); un'impersonation iniziata o finita altrove la shell la scopre senza
 * ricaricarsi (`AppComponent.initImpersonateAutoRefresh`), e allora la risposta di prima non vale più: si
 * chiede di nuovo. Ogni errore, 503 e 403 del controllo dell'impersonation compresi, vale «non gestisce»
 * ma non resta in memoria: la richiesta dopo riprova.
 */
@Injectable({ providedIn: 'root' })
export class NotificationTemplatesAccessService {
  private readonly _templates = inject(NotifierTemplatesService);
  private readonly _capabilities = signal<NotificationDesignCapabilitiesDto | null>(null);
  private _pending: Observable<boolean> | null = null;
  /** Cresce a ogni cambio di identità: una risposta chiesta prima non si mette in memoria. */
  private _generation = 0;

  /** True solo dopo una risposta del Notifier che concede la gestione. */
  readonly canManage = computed(() => this._capabilities()?.canManageTemplates === true);

  /** Sessione di impersonation in sola lettura: vede testi, storico e anteprima, ma non scrive. */
  readonly readOnly = computed(() => this.canManage() && this._capabilities()?.readOnly === true);

  constructor() {
    inject(AppProperty).isImpersonateActive
      .pipe(
        map(active => active === true || active === 'true'),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.identityChanged());
  }

  /** Chiede la capability se non c'è ancora; più chiamate insieme condividono la stessa richiesta. */
  ensureLoaded(): Observable<boolean> {
    if (this._capabilities()) {
      return of(this.canManage());
    }
    if (!this._pending) {
      const generation = this._generation;
      const request: Observable<boolean> = this._templates.getCapabilities().pipe(
        map(capabilities => {
          const loaded = {
            canManageTemplates: capabilities?.canManageTemplates === true,
            readOnly: capabilities?.readOnly === true,
          };
          if (generation === this._generation) this._capabilities.set(loaded);
          return loaded.canManageTemplates;
        }),
        catchError(() => of(false)),
        finalize(() => {
          if (this._pending === request) this._pending = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this._pending = request;
    }
    return this._pending;
  }

  /**
   * L'utente è un altro: la capability di prima, o quella in arrivo, non vale. Se era già stata chiesta
   * si chiede subito per l'utente nuovo, perché la voce di menu e la sola lettura dell'editor non
   * aspettino una navigazione.
   */
  private identityChanged(): void {
    const asked = this._capabilities() !== null || this._pending !== null;
    this._generation++;
    this._pending = null;
    this._capabilities.set(null);
    if (asked) this.ensureLoaded().subscribe();
  }
}
